import {
  useCreateBankAccountMutation,
  useLazyGetThirdPartySuggestionsListQuery,
  useUpdateBankAccountMutation,
} from '@/api'
import { Autocomplete, DrawerBox, Input } from '@/components'
import { useDrawer, useModal, useRole } from '@/hooks'
import { OrganizationContext } from '@/modules/Organization/Organization'
import { DrawerType, ModalType } from '@/store/app'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { useCallback, useContext, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { styles } from './styles'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const BankAccount = ({ refetchBanks, bank, isFirstBank }: any) => {
  const { hideDrawer, drawerOpened } = useDrawer()
  const { showModal } = useModal()
  const { organization, organizationId, refetchOrganization } =
    useContext(OrganizationContext)

  const [updateBank] = useUpdateBankAccountMutation()

  const [getCompanies, { data: companies }] =
    useLazyGetThirdPartySuggestionsListQuery()
  const companiesAutocomplateHandle = useCallback(
    (value: string) => {
      getCompanies(value ? value : '')
    },
    [getCompanies]
  )
  const companiesOptions = companies?.suggestions?.map((item) => ({
    label: item.data.name.short,
    value: item.data.ogrn,
    status: item.data.state.status,
    short: item.data.opf.short,
    inn: item.data.inn,
    address: item.data.address.value,
  }))

  const statusOptions = [
    {
      label: 'Действует',
      value: 'CONFIRMED',
    },
    {
      label: 'Черновик',
      value: 'DRAFT',
    },
    {
      label: 'Удалён',
      value: 'DELETED',
    },
  ]

  const methods = useForm()
  const watchMethods = methods.watch(['bank', 'number', 'status'])
  const defaults = {
    bank: {
      label: bank?.bank_name,
      value: bank?.id,
    },
    status: statusOptions?.find((e) => e?.value === bank?.status),
    number: bank?.number,
  }

  useEffect(() => {
    if (bank !== null) {
      methods.reset(defaults)
    } else {
      methods.reset({
        number: undefined,
        bank: undefined,
        status: undefined,
      })
    }
  }, [bank])

  const [createBank] = useCreateBankAccountMutation()
  //const [updateBank] = useUpdateBankAccountMutation()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (value: any) => {
    if (
      organization?.id &&
      value.bank.label &&
      value.number?.length === 20 &&
      (access ? value.status.value : true)
    ) {
      if (bank) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          number: value.number,
          bank_name: value.bank.label,
          name: value.bank.label,
          status: access ? value.status.value : 'DRAFT',
          is_main: bank.is_main,
        }
        Object.keys(payload).forEach((e) => {
          if (JSON.stringify(payload[e]) === JSON.stringify(bank[e])) {
            delete payload[e]
          }
        })
        updateBank({
          companyId: bank.companyId,
          accountId: bank.id,
          payload: payload,
        }).then(() => {
          refetchBanks()
        })
      } else {
        createBank({
          companyId: organization?.id,
          payload: {
            number: value.number,
            bank_name: value.bank.label,
            name: value.bank.label,
            status: access ? value.status.value : 'DRAFT',
            is_main: isFirstBank,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).then(() => {
          showModal(ModalType.ORGANIZATION_COMPLETE_BANK_ACCOUNT)
          refetchBanks()
          refetchOrganization()
        })
      }
      hideDrawer()
    }
  }

  const { access } = useRole('ORGANIZATION', ['ADMIN'])

  return (
    <DrawerBox
      onClose={hideDrawer}
      opened={DrawerType.ORGANIZATION_BANK_ACCOUNT === drawerOpened}
      bottom={
        access ? (
          <Stack gap={1.5}>
            <Button
              onClick={methods.handleSubmit(onSubmit)}
              variant={'contained'}
              //color="info"
              type={'submit'}
              disabled={
                !(
                  organization?.id &&
                  watchMethods[0] &&
                  watchMethods[1]?.length === 20 &&
                  (access ? watchMethods[2] : true)
                )
              }
            >
              Сохранить
            </Button>
            <Button variant={'contained'} color={'primary'}>
              Отмена
            </Button>
          </Stack>
        ) : undefined
      }
      title={'Банковский счет'}
    >
      <FormProvider {...methods}>
        <form>
          <Grid spacing={3} container>
            <Grid item sm={12}>
              {access && (
                <Typography mb={1} sx={styles.control.label}>
                  Поля для обязательного заполнения *
                </Typography>
              )}
              <Stack gap={2}>
                {access ? (
                  <Autocomplete
                    id={''}
                    //size="small"
                    name={'bank'}
                    options={companiesOptions || []}
                    label={'Банк *'}
                    placeholder={'Укажите БИК или Наименование'}
                    onInputChange={(_, data) => {
                      companiesAutocomplateHandle(data)
                    }}
                  />
                ) : (
                  <Stack>
                    <Typography mb={1} sx={styles.control.label}>
                      Банк
                    </Typography>
                    {bank?.bank_name}
                  </Stack>
                )}
                {access ? (
                  <Input
                    fullWidth
                    name="number"
                    label={'Расчетный счет *'}
                    placeholder={'Номер счёта'}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: 20 }}
                  />
                ) : (
                  <Stack>
                    <Typography mb={1} sx={styles.control.label}>
                      Расчетный счет
                    </Typography>
                    {bank?.number}
                  </Stack>
                )}
                {access && (
                  <Autocomplete
                    id={''}
                    name={'status'}
                    options={statusOptions || []}
                    label={'Статус *'}
                    placeholder={'Неизвестный'}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </DrawerBox>
  )
}
